'use client';

import * as React from 'react';
import type { CellData, RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';
import { useTranslations } from 'next-intl';
import { PlusCircle, XCircle, Loader2 } from 'lucide-react';
import { CheckIcon } from '@radix-ui/react-icons';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { InfiniteSelectConfig } from '@/types/base-form';

interface DataTableInfiniteFilterProps<
  TData extends RowData,
  TValue extends CellData
> {
  column: Column<TData, TValue>;
  title: string;
  infiniteConfig: InfiniteSelectConfig;
}

export function DataTableInfiniteFilter<
  TData extends RowData,
  TValue extends CellData
>({
  column,
  title,
  infiniteConfig
}: DataTableInfiniteFilterProps<TData, TValue>) {
  const tc = useTranslations('common');
  const [open, setOpen] = React.useState(false);

  const columnFilterValue = column.getFilterValue();
  const selectedValues = React.useMemo(
    () => new Set(Array.isArray(columnFilterValue) ? columnFilterValue : []),
    [columnFilterValue]
  );

  // Track labels for selected items so the trigger badge stays correct
  // even after the user searches and loaded pages change.
  const [selectedLabels, setSelectedLabels] = React.useState<
    Map<string, string>
  >(new Map());

  // Clear cached labels when the filter is cleared externally (e.g. "Reset filters")
  React.useEffect(() => {
    if (selectedValues.size === 0) {
      setSelectedLabels(new Map());
    }
  }, [selectedValues.size]);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const fetchGuardRef = React.useRef(false);

  // Reset server search when popover closes
  React.useEffect(() => {
    if (!open) {
      infiniteConfig.onSearchChange('');
    }
  }, [open, infiniteConfig]);

  // Reset fetch guard once the in-flight page request finishes
  React.useEffect(() => {
    if (!infiniteConfig.isFetchingNextPage) {
      fetchGuardRef.current = false;
    }
  }, [infiniteConfig.isFetchingNextPage]);

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el || fetchGuardRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (
      scrollHeight - scrollTop - clientHeight < 80 &&
      infiniteConfig.hasNextPage
    ) {
      fetchGuardRef.current = true;
      infiniteConfig.fetchNextPage();
    }
  }, [infiniteConfig]);

  React.useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [open, handleScroll]);

  const { options, search, onSearchChange, isLoading, isFetchingNextPage } =
    infiniteConfig;

  // When the popover is open and all loaded items fit without scrolling,
  // automatically fetch the next page so the user doesn't have to scroll
  // into empty space to trigger loading.
  React.useEffect(() => {
    if (!open || isLoading || isFetchingNextPage || !infiniteConfig.hasNextPage)
      return;
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1 && !fetchGuardRef.current) {
      fetchGuardRef.current = true;
      infiniteConfig.fetchNextPage();
    }
  }, [open, isLoading, isFetchingNextPage, infiniteConfig, options.length]);

  const onItemSelect = React.useCallback(
    (value: string, label: string, isSelected: boolean) => {
      const newValues = new Set(selectedValues);
      const newLabels = new Map(selectedLabels);
      if (isSelected) {
        newValues.delete(value);
        newLabels.delete(value);
      } else {
        newValues.add(value);
        newLabels.set(value, label);
      }
      setSelectedLabels(newLabels);
      const filterValues = Array.from(newValues);
      column.setFilterValue(filterValues.length ? filterValues : undefined);
    },
    [column, selectedValues, selectedLabels]
  );

  const allLoadedSelected = React.useMemo(
    () =>
      infiniteConfig.options.length > 0 &&
      infiniteConfig.options.every((o) => selectedValues.has(o.value)),
    [infiniteConfig.options, selectedValues]
  );

  const onToggleSelectAll = React.useCallback(() => {
    if (allLoadedSelected) {
      // Deselect all currently-loaded options
      const newValues = new Set(selectedValues);
      const newLabels = new Map(selectedLabels);
      for (const o of infiniteConfig.options) {
        newValues.delete(o.value);
        newLabels.delete(o.value);
      }
      setSelectedLabels(newLabels);
      const next = Array.from(newValues);
      column.setFilterValue(next.length ? next : undefined);
    } else {
      // Select all currently-loaded options
      const newValues = new Set(selectedValues);
      const newLabels = new Map(selectedLabels);
      for (const o of infiniteConfig.options) {
        newValues.add(o.value);
        newLabels.set(o.value, o.label);
      }
      setSelectedLabels(newLabels);
      column.setFilterValue(Array.from(newValues));
    }
  }, [
    allLoadedSelected,
    column,
    infiniteConfig.options,
    selectedValues,
    selectedLabels
  ]);

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      column.setFilterValue(undefined);
    },
    [column]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='border-dashed'>
          {selectedValues.size > 0 ? (
            <div
              role='button'
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              onClick={onReset}
              className='focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-1 focus-visible:outline-none'
            >
              <XCircle />
            </div>
          ) : (
            <PlusCircle />
          )}
          {title}
          {selectedValues.size > 0 && (
            <>
              <Separator
                orientation='vertical'
                className='mx-0.5 data-[orientation=vertical]:h-4'
              />
              <Badge
                variant='secondary'
                className='rounded-sm px-1 font-normal lg:hidden'
              >
                {selectedValues.size}
              </Badge>
              <div className='hidden items-center gap-1 lg:flex'>
                {selectedValues.size > 2 ? (
                  <Badge
                    variant='secondary'
                    className='rounded-sm px-1 font-normal'
                  >
                    {tc('nSelected', { count: selectedValues.size })}
                  </Badge>
                ) : (
                  Array.from(selectedValues).map((v) => (
                    <Badge
                      key={v}
                      variant='secondary'
                      className='rounded-sm px-1 font-normal'
                    >
                      {selectedLabels.get(v) ?? v}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[200px] p-0' align='start'>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={title}
            value={search}
            onValueChange={onSearchChange}
          />
          <div
            ref={scrollRef}
            className='max-h-[18.75rem] overflow-y-auto'
            onScroll={handleScroll}
          >
            <CommandList className='max-h-none overflow-visible'>
              {isLoading ? (
                <div className='flex items-center justify-center py-6'>
                  <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                </div>
              ) : (
                <>
                  <CommandEmpty>{tc('noResults')}</CommandEmpty>
                  {options.length > 0 && (
                    <CommandGroup>
                      <CommandItem
                        value='__select_all__'
                        onSelect={onToggleSelectAll}
                        className='font-medium'
                      >
                        <div
                          className={cn(
                            'border-primary flex size-4 items-center justify-center rounded-sm border',
                            allLoadedSelected
                              ? 'bg-primary'
                              : 'opacity-50 [&_svg]:invisible'
                          )}
                        >
                          <CheckIcon />
                        </div>
                        <span className='truncate'>
                          {allLoadedSelected
                            ? tc('deselectAll')
                            : tc('selectAll')}
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  )}
                  <CommandGroup>
                    {options.map((option) => {
                      const isSelected = selectedValues.has(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() =>
                            onItemSelect(option.value, option.label, isSelected)
                          }
                        >
                          <div
                            className={cn(
                              'border-primary flex size-4 items-center justify-center rounded-sm border',
                              isSelected
                                ? 'bg-primary'
                                : 'opacity-50 [&_svg]:invisible'
                            )}
                          >
                            <CheckIcon />
                          </div>
                          <span className='truncate'>{option.label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  {isFetchingNextPage && (
                    <div className='flex items-center justify-center py-2'>
                      <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </div>
          {selectedValues.size > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => onReset()}
                  className='justify-center text-center'
                >
                  {tc('clearFilters')}
                </CommandItem>
              </CommandGroup>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
